import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import type { ValueListSummary } from '@messe/shared'
import { Button } from '../components/ui/button.js'
import { Dialog } from '../components/ui/dialog.js'
import { CheckboxField, TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { format, t } from '../i18n/index.js'
import { createValueList, listValueLists, updateValueList } from '../lib/value-lists.js'

/**
 * Overview of every value list.
 *
 * The client-creation switch sits here rather than inside each list, because it
 * is a property of the list and the decision is made once per list, not per
 * entry.
 */
export function ValueListsPage() {
  const strings = t()
  const queryClient = useQueryClient()

  const query = useQuery({ queryKey: ['value-lists'], queryFn: listValueLists })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ key: '', name: '', description: '' })
  const [problem, setProblem] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      createValueList({
        key: draft.key,
        name: draft.name,
        description: draft.description === '' ? null : draft.description,
        allowClientCreation: false,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['value-lists'] })
      setCreating(false)
      setDraft({ key: '', name: '', description: '' })
      setProblem(null)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const toggleClientCreation = useMutation({
    mutationFn: (list: ValueListSummary) =>
      updateValueList(list.id, { allowClientCreation: !list.allowClientCreation }),
    // Applied optimistically: a switch that only moves once the server answers
    // feels broken, and people click it again.
    onMutate: async (list) => {
      await queryClient.cancelQueries({ queryKey: ['value-lists'] })
      const previous = queryClient.getQueryData<ValueListSummary[]>(['value-lists'])

      queryClient.setQueryData<ValueListSummary[]>(['value-lists'], (current) =>
        current?.map((entry) =>
          entry.id === list.id
            ? { ...entry, allowClientCreation: !entry.allowClientCreation }
            : entry,
        ),
      )

      return { previous }
    },
    onError: (_error, _list, context) => {
      // Put the switch back where it was; the change did not happen.
      if (context?.previous) queryClient.setQueryData(['value-lists'], context.previous)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['value-lists'] })
    },
  })

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{strings.valueLists.heading}</h1>
          <p className="text-text-muted mt-1 text-sm">{strings.valueLists.intro}</p>
        </div>
        <Button onClick={() => setCreating(true)}>{strings.valueLists.createList}</Button>
      </header>

      {problem !== null && <Alert tone="danger">{problem}</Alert>}

      {query.isError && <Alert tone="danger">{strings.valueLists.loadError}</Alert>}
      {query.isPending && <p className="text-text-muted text-sm">{strings.app.loading}</p>}

      {query.data && (
        <div className="grid gap-4 sm:grid-cols-2">
          {query.data.map((list) => (
            <article
              key={list.id}
              className="border-border bg-surface-raised flex flex-col gap-3 rounded-lg border p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-medium">{list.name}</h2>
                  <p className="text-text-muted text-xs">
                    <code>{list.key}</code>
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {list.system && <Badge tone="info">{strings.valueLists.systemBadge}</Badge>}
                  {list.pendingCount > 0 && (
                    <Badge tone="warning">
                      {format(strings.valueLists.pendingBadge, { count: list.pendingCount })}
                    </Badge>
                  )}
                </div>
              </div>

              {list.description && <p className="text-text-muted text-sm">{list.description}</p>}

              <p className="text-text-muted text-sm">
                {list.itemCount} {strings.valueLists.entries}
              </p>

              <CheckboxField
                label={strings.valueLists.allowClientCreation}
                hint={strings.valueLists.allowClientCreationHint}
                checked={list.allowClientCreation}
                // Not disabled while saving: the optimistic update already
                // shows the new state, and disabling mid-click makes the switch
                // feel stuck.
                onChange={() => {
                  toggleClientCreation.mutate(list)
                }}
              />

              <div className="mt-1">
                <Link to="/stammdaten/$listId" params={{ listId: list.id }}>
                  <Button variant="secondary" size="sm">
                    {strings.valueLists.open}
                  </Button>
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        title={strings.valueLists.createList}
        description={strings.valueLists.createListHint}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              {strings.valueLists.actions.cancel}
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={
                create.isPending || draft.name.trim() === '' || !/^[a-z][a-z0-9_]*$/.test(draft.key)
              }
            >
              {create.isPending
                ? strings.valueLists.actions.saving
                : strings.valueLists.actions.save}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <TextField
            label={strings.valueLists.form.label}
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />
          <TextField
            label={strings.valueLists.listKey}
            hint={strings.valueLists.listKeyHint}
            value={draft.key}
            onChange={(event) => setDraft({ ...draft, key: event.target.value })}
          />
          <TextField
            label={strings.valueLists.listDescription}
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          />
        </div>
      </Dialog>
    </div>
  )
}
